/**
 * Keeps the camera preview in step with the editor: each frame it works out
 * whether the selection is a camera and what it sees (cameraPreview.ts), tells
 * the renderer when that changed, and publishes it for the overlay that frames
 * the preview and names the camera. Runs in the frame loop's "bridge" phase,
 * after the RenderBridge, so a moving camera's preview moves with it.
 */
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { RenderBackend } from "../../render/RenderBackend";
import type { EntityId } from "../../world/entity";
import type { TransformSystem } from "../../world/TransformSystem";
import type { WorldReader } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import type { SettingsValues } from "../settings/EditorSettings";
import { cameraPreviewFor } from "./cameraPreview";
import type { CameraPreview } from "./cameraPreview";
import type { ViewportCamera } from "./ViewportCamera";

export interface CameraPreviewDeps {
  readonly backend: Pick<RenderBackend, "setCameraPreview">;
  readonly world: WorldReader;
  readonly transforms: TransformSystem;
  readonly selection: ReadableStore<SelectionState>;
  readonly settings: ReadableStore<SettingsValues>;
  readonly camera: ViewportCamera;
  readonly piloting: ReadableStore<EntityId | null>;
}

export class CameraPreviewController implements ReadableStore<CameraPreview | null> {
  private readonly store: VersionedStore<CameraPreview | null>;
  /** The last preview sent, serialized: previews are small, and a frame usually changes nothing. */
  private sent = "null";

  constructor(
    private readonly deps: CameraPreviewDeps,
    scheduler: ChangeScheduler,
  ) {
    this.store = new VersionedStore<CameraPreview | null>(scheduler, null);
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): CameraPreview | null => this.store.getSnapshot();

  flush(): void {
    const { world, transforms, selection, settings, camera } = this.deps;
    const preview = cameraPreviewFor({
      world,
      worldMatrixOf: (id) => transforms.worldMatrix(id),
      selection: selection.getSnapshot(),
      gameView: settings.getSnapshot().gameView,
      piloting: this.deps.piloting.getSnapshot(),
      viewport: camera.getSnapshot(),
      viewportSize: camera.viewportSize,
    });

    const serialized = JSON.stringify(preview);
    if (serialized === this.sent) return;
    this.sent = serialized;
    this.store.publish(preview);
    this.deps.backend.setCameraPreview(preview && { camera: preview.camera, rect: preview.rect });
  }
}
